import debounce from 'debounce-promise';
import { makeAutoObservable } from 'mobx';
import { z } from 'zod';
import { UserRoles } from '../../../store/constants';
import type { StringDto } from '../../../store/utils/Dto';
import extractValue, {
  type EventOrValue,
} from '../../../store/utils/extractValue';
import Indicator from '../../../store/utils/Indicator';
import Validator from '../../../store/utils/Validator';
import Configurations, {
  type ConfigurationsDto,
} from '../../account/store/Configurations';

export default class User {
  static Schema = z
    .object({
      // Legacy accounts hold invalid emails
      email: z.string().min(1),
      name: z.string().default(''),
      password: z.string().optional(),
      role: z.enum(UserRoles as [string, ...string[]]),
      savingAction: z.any(),
    })
    .superRefine((data, ctx) => {
      if (data.savingAction !== 'create') {
        return;
      }

      if (!z.email().safeParse(data.email).success) {
        ctx.addIssue({
          code: 'custom',
          path: ['email'],
          message: 'must be a valid email address',
        });
      }

      if (!data.password || data.password.length < 6) {
        ctx.addIssue({
          code: 'custom',
          path: ['password'],
          message: 'length must be at least 6 characters long',
        });
      }
    });

  id = '';
  disabledAt = '';
  email = '';
  name = '';
  password?: string;
  role: Role = 'CUSTOMER';
  configurations = {} as Configurations;

  savingAction: SavingAction = 'update';
  readonly indicator = new Indicator();

  private validator = new Validator(this, User.Schema);
  private readonly onSaveDelayed = debounce(this.save, 1000);

  constructor(
    { configurations, ...dto }: UserDto,
    private readonly onSave: (user: User) => Promise<void>,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    Object.assign(this, dto);
    this.configurations = new Configurations(configurations, this.saveDelayed);
    this.validator.validate();
  }

  get displayName(): string {
    return this.name || this.email;
  }

  get slugifiedName(): string {
    const withoutTopDomain =
      this.name || this.email.slice(0, this.email.lastIndexOf('.'));
    return withoutTopDomain.toLowerCase().replaceAll(/\W+/g, '-');
  }

  get errors(): StringDto<UserDto> {
    return this.validator.errorsForEnabledFields;
  }

  get disabled(): boolean {
    return Boolean(this.disabledAt);
  }

  set disabled(value: boolean) {
    this.disabledAt = value ? new Date().toISOString() : '';
  }

  get isCustomer() {
    return this.role === 'CUSTOMER';
  }

  get isProvider() {
    return this.role === 'PROVIDER' || this.role === 'ADMIN';
  }

  get allErrorsAsStrings(): string[] {
    return this.validator.allErrorsAsStrings;
  }

  get hasErrors(): boolean {
    return !this.validator.isValid;
  }

  handleChange(e: EventOrValue) {
    const { name, value } = extractValue(e);

    Object.assign(this, { [name]: value });

    this.validator.validate();
  }

  handleBlur(e: EventOrValue<keyof UserDto>) {
    const { name } = extractValue(e);
    this.validator.enableFieldValidation(name);
  }

  async save() {
    return this.indicator.spyOnPromise(this.onSave(this));
  }

  async saveDelayed() {
    this.indicator.setState('processing');
    return this.onSaveDelayed(this);
  }

  update(dto: UserDto) {
    this.id = dto.id || '';
  }

  setSavingAction(action: SavingAction) {
    this.savingAction = action;
  }

  toJSON(): Required<UserDto> {
    return {
      configurations: this.configurations.toJSON(),
      disabledAt: this.disabledAt || null,
      email: this.email,
      name: this.name,
      id: this.id,
      password: this.password || (undefined as unknown as string),
      role: this.role,
    };
  }
}

export interface UserDto {
  configurations?: ConfigurationsDto;
  disabledAt?: null | string;
  email: string;
  id?: string;
  name?: string;
  password: string;
  role: string;
}

export type Role = 'ADMIN' | 'CUSTOMER' | 'PROVIDER';

type SavingAction = 'create' | 'update';
